import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { Printer, Download, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { SummaryCards } from '../components/SummaryCards';
import { WorkstreamLegend } from '../components/WorkstreamLegend';
import { WorkstreamManager } from '../components/WorkstreamManager';
import { ProjectTable } from '../components/ProjectTable';
import { ShareDialog } from '../components/ShareDialog';
import { EditableCell } from '../components/EditableCell';
import { ConnectionStatus } from '../components/ConnectionStatus';
import { BoardDetailSkeleton } from '../components/LoadingSkeleton';
import { NoProjectsState } from '../components/EmptyState';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function BoardDetail() {
  const { boardId } = useParams();
  const { refreshBoards } = useOutletContext() || {};
  const { authedFetch, token, isEditor, isViewer } = useAuth();
  const [board, setBoard] = useState(null);
  const [projects, setProjects] = useState([]);
  const [workstreams, setWorkstreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [presenceCount, setPresenceCount] = useState(0);
  const wsRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [boardData, projectsData, wsData] = await Promise.all([
        authedFetch(`/api/boards/${boardId}`),
        authedFetch(`/api/boards/${boardId}/projects`),
        authedFetch(`/api/boards/${boardId}/workstreams`),
      ]);
      setBoard(boardData);
      setProjects(projectsData);
      setWorkstreams(wsData);
    } catch (e) {
      setError(e.message || 'Failed to load board');
      toast.error('Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [boardId, authedFetch]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // WebSocket connection with exponential backoff
  useEffect(() => {
    const wsUrl = api.getWsUrl(`/api/ws/board/${boardId}`);
    let ws;
    let reconnectTimer;
    const reconnectAttemptsRef = { current: 0 };

    const connect = () => {
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setWsConnected(true);
        reconnectAttemptsRef.current = 0;
        // Request initial presence count
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'get_presence' }));
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'project_updated' || msg.type === 'project_created' || msg.type === 'project_deleted') {
            fetchData();
          } else if (msg.type === 'presence_count') {
            setPresenceCount(msg.count || 0);
          }
        } catch (err) {
          console.error('WS message parse error:', err);
        }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        reconnectAttemptsRef.current++;
        // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
        reconnectTimer = setTimeout(connect, delay);
      };
      
      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        ws.close();
      };
      
      wsRef.current = ws;
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [boardId, fetchData]); // Added fetchData to dependencies

  // Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        fetchData();
        toast.success('Saved');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [fetchData]);

  const handleBoardNameChange = async (name) => {
    try {
      await authedFetch(`/api/boards/${boardId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
      setBoard((b) => ({ ...b, name }));
      refreshBoards?.();
      window.dispatchEvent(new Event('boards-changed'));
    } catch (e) {
      toast.error('Failed to rename board');
    }
  };

  const handleExport = (format) => {
    const accessToken = localStorage.getItem('accessToken');
    const url = `${api.baseUrl}/api/boards/${boardId}/export?format=${format}`;
    // Use fetch with auth header, then create download
    fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Export failed');
        const filename = res.headers.get('content-disposition')?.match(/filename=(.+)/)?.[1] || `export.${format}`;
        return res.blob().then((blob) => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Export failed'));
  };

  if (loading) {
    return <BoardDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="text-destructive mb-4">Error loading board</div>
        <Button onClick={() => {
          setLoading(true);
          fetchData();
        }}>
          Retry
        </Button>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Board not found</div>
      </div>
    );
  }

  return (
    <div className="print-full-width">
      {/* Top bar */}
      <div className="border-b bg-card px-6 py-4 no-print">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {isEditor ? (
              <EditableCell
                value={board.name}
                field="name"
                projectId="board"
                onSave={handleBoardNameChange}
                className="font-heading text-xl font-bold tracking-tight"
              />
            ) : (
              <h1 className="font-heading text-xl font-bold tracking-tight">{board.name}</h1>
            )}
            <ConnectionStatus connected={wsConnected} presenceCount={presenceCount} />
          </div>

          <div className="flex items-center gap-2">
            {isEditor && <ShareDialog boardId={boardId} />}

            {isEditor && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="board-toolbar-export-button">
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('csv')}>Export CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('json')}>Export JSON</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" />
              Print
            </Button>

            <Link to={`/boards/${boardId}/activity`}>
              <Button variant="ghost" size="sm" className="gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                Activity
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Board content */}
      <div className="px-6 py-6 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <WorkstreamLegend workstreams={workstreams} />
          {isEditor && (
            <WorkstreamManager boardId={boardId} workstreams={workstreams} onChange={fetchData} />
          )}
        </div>
        <SummaryCards summary={board.summary} />
        <ProjectTable
          projects={projects}
          workstreams={workstreams}
          boardId={boardId}
          onRefresh={fetchData}
          readOnly={isViewer}
        />
      </div>
    </div>
  );
}
